import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Tooltip with `label`, shown on hover AND on click.
//   hover  -> shows while the mouse is over the text
//   click  -> pins it open (click again to close), works on touch screens too
// Closes on outside click, Esc, scroll or resize.
// Rendered in a portal (position: fixed) so table/overflow wrappers never clip it.
export default function ClickTooltip({ label, children, className = "" }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null); // { x, y, below } while visible
  const [pinned, setPinned] = useState(false);

  const hide = () => {
    setPos(null);
    setPinned(false);
  };

  useEffect(() => {
    if (!pos) return undefined;
    const onKey = (e) => e.key === "Escape" && hide();
    const onDown = (e) => {
      if (!ref.current?.contains(e.target)) hide();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [pos]);

  if (!label) return <span className={className}>{children}</span>;

  const show = () => {
    const r = ref.current.getBoundingClientRect();
    const below = r.top < 48; // no room above -> show under the text
    setPos({ x: r.left + r.width / 2, y: below ? r.bottom + 6 : r.top - 6, below });
  };

  const handleClick = () => {
    if (pinned) {
      hide();
    } else {
      setPinned(true);
      show();
    }
  };

  return (
    <>
      <span
        ref={ref}
        role="button"
        tabIndex={0}
        className={className}
        style={{ cursor: "pointer" }}
        onMouseEnter={show}
        onMouseLeave={() => !pinned && hide()}
        onFocus={show}
        onBlur={() => !pinned && hide()}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {children}
      </span>

      {pos &&
        createPortal(
          <div
            role="tooltip"
            className="position-fixed bg-dark text-white small rounded px-2 py-1 shadow"
            style={{
              left: pos.x,
              top: pos.y,
              transform: pos.below ? "translateX(-50%)" : "translate(-50%, -100%)",
              zIndex: 3000,
              whiteSpace: "nowrap",
              pointerEvents: "none",
            }}
          >
            {label}
          </div>,
          document.body
        )}
    </>
  );
}